import type { ReactElement } from 'react'

import { LinkText } from '@/components/elements/LinkText'
import { SectionTitle } from '@/components/elements/SectionTitle'
import { Spacer } from '@/components/elements/Spacer'
import { TitleLayout } from '@/components/layout'
import { Head } from '@/components/layouts/Head'
import { Layout } from '@/components/layouts/Ir'
import { BulletedList } from '@/components/layouts/Ir/BulletedList'
import { AHeading, BHeading } from '@/components/layouts/Ir/Heading'
import { Paragraph } from '@/components/layouts/Ir/Paragraph'

export const PolicyPage = () => {
  return (
    <Layout>
      <SectionTitle
        titleText="Stakeholder Engagement"
        subTitleText="ステークホルダーに関わる施策"
        color="normal"
      />
      <Spacer h={{ sp: 70, pc: 120 }} />
      <AHeading>ステークホルダーの立場の尊重に係る取組み状況</AHeading>
      <BHeading>社内規程等によりステークホルダーの立場の尊重について規定</BHeading>
      <Paragraph>
        当社グループはパーパス（当社グループの存在目的）として『世界の誰もが「豊かさ」を享受できる社会をつくる。』を掲げ、当社グループの発展・成長と、社会全体の発展・成長の2つの価値の最大化に取り組んでおります。
      </Paragraph>
      <Paragraph>
        また、お客様をはじめ、お取引先、株主、従業員等の当社グループをとりまくステークホルダーに対する責任を果たし、高い企業倫理と誠実さを持って行動することを「TRIALの行動指針」にて定めております。
      </Paragraph>
      <Spacer h={{ sp: 50, pc: 80 }} />
      <BHeading>環境保全活動、CSR活動等の実施</BHeading>
      <Paragraph>
        当社グループは創業以来、「流通小売業界のムダ・ムラ・ムリを削減し、ローコストを実現することで、お客様・社会の役に立つ」ことを企業経営の根幹(DNA)として受け継いできており、実践してまいりました。2021年には、創業以来のDNAを受け継ぐかたちで、パーパスとして『世界の誰もが「豊かさ」を享受できる社会をつくる。』、ビジョンとして「テクノロジーと、人の経験知で、世界のリアルコマースを変える。」を策定しております。この創業時から培ってきたDNAのもと、パーパス・ビジョンを、他社と協業し企業の垣根を越えて実現し、当社グループの発展・成長と、社会全体の発展・成長の2つの価値の最大化に取り組んでいくことを当社グループにおけるサステナビリティの基本方針としております。上記サステナビリティの基本方針に基づき、以下のとおり環境・社会課題の解決に取り組んでおります。
      </Paragraph>
      <Spacer h={{ sp: 30, pc: 50 }} />
      <BulletedList>
        <li>
          当社グループが開発したデータ分析基盤システム（MD-Link）の活用によって、サプライチェーン（メーカー、卸、小売）間で販売データを共有・分析し、ムダのない生産・調達計画を実現することで、物流の効率化や商品の廃棄ロスの低減に取組んでおります。
        </li>
        <li>
          ガリレイ株式会社と協同で、AI技術を活用した店内の空調や冷凍・冷蔵ケースの省エネ化に取組んでおります(2019年度省エネ大賞経済産業大臣賞を受賞)。
        </li>
        <li>
          2022年8月に九州物流研究会、2023年5月に北海道物流研究会、2024年9月には東北物流みらい研究会を発足し、企業横断で物流を取り巻く「2024年問題」や「脱炭素」等の課題解決に向けた検討を進めております。
        </li>
        <li>
          一部の店舗において店舗の屋根に太陽光パネルを設置することで、再生可能エネルギーの活用を進めております。
        </li>
        <li>
          AIカメラと電子棚札を活用し、惣菜の消費期限に応じた自動値下げによるフードロス削減の取組みを始めております。
        </li>
        <li>
          持続的な成長を実現するため、従業員・パート・アルバイトが働きやすく、働きがいを感じられる職場環境の構築を進めております。能力に応じた給与・時給体系の整備、貢献度・期待値に応じた株式報酬制度の導入、創業者及び経営陣幹部が主導する教育プログラムの実施、海外視察研修などに取り組んでおります。また、DEIB（Diversity,Equity, Inclusion &Belonging）推進委員会を設置し、女性、外国人、シニア層、障がいのある従業員など、多様な人材が能力を発揮し、活躍できる職場環境づくりを推進しております。
        </li>
        <li>
          福岡県宮若市と連携し、小・中学校などの公共施設の跡地を利用して、世界中から研究者や技術者が集まるリテールDXの開発拠点の構築を推進しており、地域社会の活性化にも寄与しております。
        </li>
        <li>
          当社グループが開発した商圏分析ソフト「RetailMap」の活用などによる全国各地への新規出店の推進及び既存店の活性化により、地域社会の活性化や雇用の創出に貢献しております。
        </li>
      </BulletedList>
      <Spacer h={{ sp: 50, pc: 80 }} />
      <BHeading>ステークホルダーに対する情報提供に係る方針等の策定</BHeading>
      <Paragraph>
        当社は、「
        <LinkText
          path="/ir/manage/disclosure/"
          text="ディスクロージャー・IRポリシー"
        />
        」を策定し、ウェブサイトにて公表しております。本ポリシーに従い、ステークホルダーに対する適時適切な情報開示に努めてまいります。
      </Paragraph>
    </Layout>
  )
}

PolicyPage.getLayout = (page: ReactElement) => {
  const breadCrumbs = [
    { name: 'ホーム', path: '/' },
    { name: '株主・投資家向け情報', path: '/ir/' },
    { name: 'コーポレート・ガバナンス', path: '/ir/governance/' },
    { name: 'ステークホルダーに関わる施策', path: '' },
  ]

  return (
    <>
      <Head
        title="ステークホルダーに関わる施策"
        description="トライアルホールディングスのステークホルダーの立場の尊重に係る取組み状況について掲載しています。"
        ogp={{ url: 'ir/governance/policy/' }}
      />
      <TitleLayout
        title="Investor Relations"
        subTitle="株主・投資家向け情報"
        breadCrumbs={breadCrumbs}
      >
        {page}
      </TitleLayout>
    </>
  )
}
